import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { Menu, MenuItem, IconButton, ReadMoreIcon } from '@gogame/components';
import { useParams, useNavigate } from '@gogame/screens';

import { useGroup } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Group';
import { useLoader } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Loader';
import {
  useViewStateByPath,
  useParamsEntityIds,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/ViewState';
import { NODE_LEVELS } from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';
import { useCreateNodesMarketingPlan } from 'scenes/MarketingPlanning/MarketingPlanningWizard/hooks';
import {
  loadDefaultApplications,
  loadDefaultChannels,
  loadDefaultPlatforms,
  loadDefaultAffiliates,
  loadDefaultOwners,
  loadDefaultCountries,
  loadDefaultManagers,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/sources/entities';

const loadDefaultEntitiesSources = [
  null,
  null,
  loadDefaultApplications,
  loadDefaultChannels,
  loadDefaultPlatforms,
  loadDefaultAffiliates,
  loadDefaultCountries,
  loadDefaultManagers,
  loadDefaultOwners,
];

const SplitBy = () => {
  const { year, month } = useParams();
  const { levelPath } = useGroup();
  const { processAsync } = useLoader();

  const { id, entityId, depth, activityTypes } = useViewStateByPath(levelPath);

  const {
    networkId,
    applicationId,
    platformId,
    affiliateId,
    channelId,
    countryId,
    managerId,
  } = useParamsEntityIds();

  const createNodes = useCreateNodesMarketingPlan(processAsync);

  const splitByList = React.useMemo(
    () =>
      NODE_LEVELS.map((level, i) => ({
        id: i,
        name: level,
      })).splice(depth + 1),
    [depth]
  );

  const navigate = useNavigate();

  const handleSplitBy = React.useCallback(
    async (_e, value) => {
      const entities = await processAsync(
        loadDefaultEntitiesSources[value]({
          year,
          month,
          networkId: depth === 1 ? entityId : networkId,
          applicationId: depth === 2 ? entityId : applicationId,
          channelId: depth === 3 ? entityId : channelId,
          platformId: depth === 4 ? entityId : platformId,
          affiliateId: depth === 5 ? entityId : affiliateId,
          countryId: depth === 6 ? entityId : countryId,
          managerId: depth === 7 ? entityId : managerId,
        })
      );

      const entityIds = Array.isArray(entities)
        ? entities.map(({ id }) => id)
        : null;

      const nodes = await createNodes({
        parentId: id,
        depth: value,
        levelPath,
        entityIds,
        node: { activityTypes },
      });

      const to = isEmpty(nodes)
        ? `${entityId}`
        : [...Array(value - depth).keys()]
            .map((i) => (i ? '-1' : entityId))
            .join('/');

      navigate(to);
    },
    [id, levelPath, depth, year, month, entityId, activityTypes]
  );

  const handleSplit = React.useCallback((e) => handleSplitBy(e, 2), []);

  return depth === 1 ? (
    <IconButton
      icon={<ReadMoreIcon />}
      tooltip={'Split'}
      onClick={handleSplit}
    />
  ) : (
    <Menu anchor={<IconButton icon={<ReadMoreIcon />} tooltip={'Split by'} />}>
      {splitByList.map(({ id, name }) => {
        return (
          <MenuItem key={id} onClick={(e) => handleSplitBy(e, id)}>
            {name}
          </MenuItem>
        );
      })}
    </Menu>
  );
};

export default SplitBy;
